import React, { Component } from 'react';
import { Alert } from '@betareader/design-system';
import { Link, withRouter } from 'react-router-dom';
import queryString from 'query-string';

// redux
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { updateUser } from '../../modules/user';

import firebase from '../../config/fire';
import { getVerificationReturnPath } from './verificationReturnPath';

export class VerifyEmail extends Component {
  state = {
    error: '',
    errorCode: '',
    validCode: null,
    verifiedCode: false,
  };

  componentDidMount() {
    // Try to apply the email verification code.
    firebase
      .auth()
      .applyActionCode(this.props.actionCode)
      .then(
        async () => {
          // Email address has been verified (or email changed + verified).
          if (firebase.auth().currentUser) {
            try {
              await firebase.auth().currentUser.reload();
              // Force a fresh ID token so subsequent API calls in any
              // open tab carry email_verified=true immediately.
              const freshToken = await firebase
                .auth()
                .currentUser.getIdToken(true);
              localStorage.setItem('br-storage-key', freshToken);
            } catch (e) {
              // non-fatal
            }
            this.props.updateUser(firebase.auth().currentUser);
          }
          this.setState({ validCode: true, verifiedCode: true });

          // Honor continueUrl from actionCodeSettings.url so the user lands
          // back on the page they were trying to reach.
          const parsed = queryString.parse(this.props.location.search);
          if (typeof parsed.continueUrl === 'string' && parsed.continueUrl) {
            setTimeout(() => {
              window.location.replace(
                getVerificationReturnPath(parsed.continueUrl)
              );
            }, 1500);
          }
        },
        error => {
          // Code is invalid or expired. Ask the user to verify their email address
          // again.
          this.setState({
            error: error.message,
            errorCode: error.code,
            validCode: false,
            verifiedCode: true,
          });
        }
      );
  }

  render() {
    const { error, errorCode, validCode, verifiedCode } = this.state;

    const { user } = this.props;

    const parsed = queryString.parse(this.props.location.search);
    const mode = typeof parsed.mode === 'string' ? parsed.mode : '';
    const continuePath = getVerificationReturnPath(parsed.continueUrl);
    const recoveryUrl = `/verify-email?redirect=${encodeURIComponent(continuePath)}`;
    const isExpired = errorCode === 'auth/expired-action-code';
    const isInvalid = errorCode === 'auth/invalid-action-code';
    const isEmailChange = mode === 'verifyAndChangeEmail';
    const successHeader = isEmailChange
      ? 'Your new email has been confirmed'
      : 'Your email has been verified';

    let component;
    if (user && user.emailVerified && !verifiedCode) {
      component = (
        <Alert variant="success">
          <h2>Your email has already been verified</h2>
          <span>
            You can now <Link to="/profile">sign in</Link> to your account.
          </span>
        </Alert>
      );
    } else if (!verifiedCode) {
      component = <p role="status">Verifying your email…</p>;
    } else if (verifiedCode && validCode) {
      component = (
        <Alert variant="success">
          <h2>{successHeader}</h2>
          <span>
            You can now <Link to="/profile">sign in</Link> with your account.
          </span>
        </Alert>
      );
    } else if (verifiedCode && !validCode) {
      component = (
        <Alert variant="error">
          <h2>Verification failed</h2>
          <p>
            {isExpired
              ? 'This verification link has expired.'
              : isInvalid
                ? 'This verification link is invalid or has already been used.'
                : error}
          </p>
          {mode === 'verifyEmail' ? (
            <>
              <p>
                Request a new link and open the latest verification email to
                continue reading.
              </p>
              <Link
                className="br-button br-button--primary br-button--medium"
                to={recoveryUrl}
              >
                Request a new verification email
              </Link>
              <p>
                If asked to sign in, use the account whose email you want to
                verify.
              </p>
            </>
          ) : (
            <p>
              Check your <Link to="/profile">account profile</Link> to see your
              current email settings and request a new confirmation there.
            </p>
          )}
        </Alert>
      );
    }

    return component;
  }
}

// redux stuff
const mapStateToProps = state => ({
  user: state.user.user,
  userProfile: state.user.userProfile,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      updateUser,
    },
    dispatch
  );

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(VerifyEmail)
);
